// Redaction for Sentry event/breadcrumb payloads. User-submitted content (source
// text, prompts) must never reach Sentry via a stray console.log/thrown-error
// breadcrumb — two independent defenses, since neither alone is sufficient:
//
//   - LENGTH: ids/status codes/counts are always short, content blobs are not.
//     Misses anything short, though, and plenty of real prompts/queries are.
//   - FIELD NAME: catches short content the length check would miss, matched
//     case-insensitively with `-`/`_` ignored (so text_input, text-input and
//     textInput are all treated as the same name) against a fixed denylist.
//     Doubles as the header/secret guard (Authorization, X-Api-Key, ...), which
//     are also short and would otherwise sail through the length check.
export const MAX_STRING_LENGTH = 300;

const SENSITIVE_KEY_NAMES = [
  // user content
  'prompt',
  'text',
  'content',
  'query',
  'input',
  'story',
  'narration',
  'description',
  'textinput',
  'videoprompt',
  // secrets/headers
  'authorization',
  'cookie',
  'setcookie',
  'xapikey',
  'xinternalapikey',
  'apikey',
  'token',
  'accesstoken',
  'refreshtoken',
  'password',
  'secret',
];
const SENSITIVE_KEYS = new Set(SENSITIVE_KEY_NAMES);

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[-_]/g, '');
}

export function redact(value: unknown, key?: string): unknown {
  if (key !== undefined && SENSITIVE_KEYS.has(normalizeKey(key))) {
    return '<redacted>';
  }
  if (typeof value === 'string') {
    return value.length > MAX_STRING_LENGTH ? `<redacted: ${value.length} chars>` : value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redact(item));
  }
  // Date/Error (and other built-ins) are not plain data objects: Date has no
  // own-enumerable properties and Error's message/stack are non-enumerable, so
  // Object.entries on either yields {} — silently discarding real debugging
  // info instead of redacting it. Pass them through rather than flattening.
  if (value instanceof Date || value instanceof Error) {
    return value;
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, redact(v, k)]),
    );
  }
  return value;
}
